import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { divisionLevelHeatMap } from "../../../../services/api-services";
import generateChartImage from "../generateChartImage";
import Remark from "../Remark";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const DivisionHeatmap = ({ selectedCompanyId, graphName }) => {
    const [divisionHeatmepDatawithoutlevel, setDivisionHeatmepDatawithoutlevel] = useState([]);
    const [remark, setRemark] = useState(""); // For remark text
    const [remarkId, setRemarkId] = useState(null); // For remarkId
    const [isAttempt, setIsAttempt] = useState();
    const [colorRanges, setColorRanges] = useState([]); // To store dynamic color ranges from API
   
    // Fetch heatmap data
    const getHeatmapdivisionDatawithoutlevel = async () => {
        try {
            const response = await divisionLevelHeatMap(selectedCompanyId, "department", graphName);
            if (response?.data?.responseCode === 200) {
                const remark = response?.data?.remark || "";
                const remarkId = response?.data?.remarkId || null;
                const isAttempt = response?.data?.isAttempt ?? 0;

                // Set remark and remarkId to state
                setRemark(remark);
                setIsAttempt(isAttempt);
                setRemarkId(remarkId);
                setDivisionHeatmepDatawithoutlevel(response?.data?.responseBody || []);
                setColorRanges(response?.data?.companyColorList || []); // Set dynamic color ranges from API
            }
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        getHeatmapdivisionDatawithoutlevel();
    }, [selectedCompanyId]);

    const categories = [];
    let data = [];

    if (divisionHeatmepDatawithoutlevel?.length >= 0) {
        divisionHeatmepDatawithoutlevel?.forEach((item) => {
            if (item.dimensionOverallHeatmapChartResponseList?.length >= 0) {
                item.dimensionOverallHeatmapChartResponseList?.forEach((dimension) => {
                    if (!categories?.includes(dimension.dimensionName)) {
                        categories?.push(dimension.dimensionName);
                    }

                    const entry = data.find((d) => d?.name === item?.name);
                    if (entry) {
                        entry?.values?.push(parseFloat(dimension?.percentage?.toFixed(2)));
                    } else {
                        data?.push({
                            name: item.name,
                            values: [parseFloat(dimension?.percentage?.toFixed(2))],
                        });
                    }
                });
            }
        });
    }

    // Calculate grand total based on non-zero data
    const grandTotal = categories.map((_, idx) => {
        const sum = data.reduce((total, row) => total + (row.values[idx] || 0), 0);
        const average = sum / data.length; // Divide by total rows (not filtered)
        return parseFloat(average?.toFixed(2));
    });

    // Function to get color for value, ensuring 0% gets the 0-10% color
    const getColorForValue = (value) => {

        for (let i = 0; i < colorRanges.length; i++) {
            const range = colorRanges[i];
            if (value >= range.rangeFrom && value <= range.rangeTo) {
                return range.colorName; // Return the corresponding color
            }
        }
        return "#000000"; // Default color if no range matches (shouldn't happen)
    };

    const grandTotalColor = grandTotal.length >= 0 ? getColorForValue(grandTotal[0]) : "#000000"; // Get color for the first grand total value

    // Assign the Grand Total to series and apply color
    const series = [
        ...data.map((row) => ({
            name: row.name,
            data: row.values,
        })),
        {
            name: "Grand Total (Average)",
            data: grandTotal,
            color: grandTotalColor,
        },
    ];

    // Define dynamic color range based on API's companyColorList
    const colorRangesArray = colorRanges.map((range) => ({
        from: range.rangeFrom,
        to: range.rangeTo,
        color: range.colorName,
        name: `${range.rangeFrom}% - ${range.rangeTo}%`,
    }));

    const options = {
        chart: {
            id: "demographic_dashboard_division",
            type: "heatmap",
            events: {
                mounted: () => {
                    setTimeout(() => {
                        generateChartImage("demographic_dashboard_division", "demographic_dashboard_division", remark); // Call the updated function here
                    }, 100);
                },
            },
            toolbar: { show: false },
        },
        plotOptions: {
            heatmap: {
                colorScale: {
                    ranges: colorRangesArray, // Use dynamic color ranges from API
                },
            },
        },
        xaxis: {
            categories: categories,
            labels: {
                style: {
                    fontSize: "12px",
                    fontWeight: "bold",
                },
            },
            position: "top",
        },
        yaxis: {
            labels: {
                style: {
                    fontSize: "12px",
                    fontWeight: "bold",
                },
                formatter: (value) => (value === "Grand Total (Average)" ? "Grand Total" : value),
            },
            reversed: true,
        },
        dataLabels: {
            enabled: true,
            formatter: (value) => `${value}%`, // Rounded to 2 decimal places
            style: {
                fontSize: "10px",
                fontWeight: "bold",
                colors: ["#000"],
            },
        },
        tooltip: {
            enabled: true,
            y: {
                formatter: (value) => `${value}%`, // Tooltip values rounded
            },
        },
        legend: {
            show: true,
            position: "top",
            markers: {
                width: 12,
                height: 12,
            },
        },
    };
    const exportDimensionPercentageSummaryToExcel = (data, fileName = "Demographic Dashboard – Division") => {
        if (!data || !Array.isArray(data)) return;

        const title = ["Demographic Dashboard – Division"];
        const headers = ["Department Name", "Dimension Name", "Percentage"];
        const rows = [];
        const percentageValues = [];

        data.forEach(role => {
            role.dimensionOverallHeatmapChartResponseList?.forEach(dimension => {
                rows.push([
                    role.name,
                    dimension.dimensionName,
                    dimension.percentage
                ]);
                percentageValues.push(dimension.percentage);
            });
        });

        // Calculate average
        const total = percentageValues.reduce((acc, val) => acc + val, 0);
        const average = percentageValues.length ? total / percentageValues.length : 0;

        // Add Grand Total row
        rows.push(["", "Grand Total (Average)", average]);

        const worksheet = XLSX.utils.aoa_to_sheet([
            title,
            [],
            headers,
            ...rows
        ]);

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Role Dimension Summary");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array"
        });

        const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        saveAs(blob, fileName);
    };


    return (
        <div className="">
            <div className="text-end">
                <button className="px-3 py-2 text-white bg-primary rounded border-0" onClick={() => exportDimensionPercentageSummaryToExcel(divisionHeatmepDatawithoutlevel)}>Download Excel</button>
            </div>
            {divisionHeatmepDatawithoutlevel.length >= 0 ? (
                <div className="chart10" data-heading="Demographic Dashboard – Division" data-remark={remark}
                >
                    <Chart options={options} series={series} type="heatmap" height={350} />
                </div>
            ) : (
                <p style={{ textAlign: "center", marginTop: "20px" }}>
                    No data available to display.
                </p>
            )}
            <Remark
                graphName="DemographicDashboardDivision"
                selectedCompanyId={selectedCompanyId}
                initialRemark={remark}
                remarkId={remarkId}
                isAttempt={isAttempt}
                setRemarkprops={setRemark}
            />
        </div>
    );
};

export default DivisionHeatmap;
